<!-- eslint-disable vue/no-v-model-argument -->
<template>
  <AntRow class="user-guide">
    <AntCol :span="24">
      <AntRow
        justify="space-between"
        class="search-row"
      >
        <AntCol :span="12">
          <AntRow :gutter="16">
            <AntCol :span="20">
              <AntInput
                v-model:value="searchText"
                placeholder="Search"
                allowClear
                @pressEnter="onSearch"
              />
            </AntCol>
            <AntCol :span="4">
              <AntButton
                type="primary"
                htmlType="submit"
                @click="onSearch"
              >
                <template #icon>
                  <SearchIcon />
                </template>
                Search
              </AntButton>
            </AntCol>
          </AntRow>
        </AntCol>

        <AntButton
          v-if="!readOnly"
          type="primary"
          htmlType="submit"
          :disabled="isEditing"
          @click="onAdd"
        >
          <template #icon>
            <AddIcon />
          </template>
          Add
        </AntButton>
      </AntRow>
      <DevDebugDataBlock v-if="isDev" title="editState" :data="editState" width="100%" maxHeight="200px" />
      <AntRow class="guide-body">
        <AntCol :span="4" class="guide-col">
          <div class="guide-list">
            <div
              v-for="item in displayedGuides"
              :key="item.id"
              :class="['guide-list-item', selectedGuideId === item.id && 'guide-list-item--selected']"
              @click="onSelectGuide(item.id)"
            >
              <span
                v-for="(frag, idx) in getHighlightFragments(item.guideName || '(Untitled)')"
                :key="`${item.id}-${idx}`"
              >
                <mark v-if="frag.highlight" class="search-highlight">{{ frag.text }}</mark>
                <template v-else>{{ frag.text }}</template>
              </span>
            </div>
            <div v-if="!displayedGuides.length" class="guide-list-empty">
              No guides found
            </div>
          </div>
        </AntCol>

        <AntCol :span="20" class="guide-col guide-detail-col">
          <template v-if="selectedGuide">
            <div :class="['guide-detail-header', !isEditing && 'guide-detail-header--bordered']">
              <AntInput
                v-if="isEditing"
                v-model:value="editState.guideName"
                class="guide-name-input"
                placeholder="Title"
                allowClear
              />
              <h3 v-else class="guide-detail-title">
                <span
                  v-for="(frag, idx) in getHighlightFragments(selectedGuide.guideName)"
                  :key="`title-${idx}`"
                >
                  <mark v-if="frag.highlight" class="search-highlight">{{ frag.text }}</mark>
                  <template v-else>{{ frag.text }}</template>
                </span>
              </h3>

              <div v-if="!readOnly" class="guide-detail-actions">
                <template v-if="isEditing">
                  <SaveIcon
                    title="Save"
                    @click="onSave"
                  />
                  <CancelIcon
                    title="Cancel"
                    @click="onCancelEdit"
                  />
                </template>
                <template v-else>
                  <EditIcon
                    title="Edit"
                    @click="onStartEdit"
                  />
                  <AntPopconfirm
                    :title="`Sure to delete ${selectedGuide.guideName}?`"
                    @confirm="onDelete"
                  >
                    <DeleteIcon title="Delete" />
                  </AntPopconfirm>
                </template>
              </div>
            </div>

            <div class="guide-editor-wrap">
              <SectionEditor
                v-if="isEditing"
                :key="`edit-${selectedGuideId}`"
                class="guide-section-editor"
                height="100%"
                :previewMode="false"
                :imageManagementData="imageManagementData"
                :sectionsDataByDocId="sectionsDataByDocId"
                :editorContent="editState.content"
                :onChange="val => { editState.content = val }"
              />
              <div v-else class="guide-editor-scroll">
                <div
                  class="section-editor viewer"
                  v-html="viewContentHtml"
                />
              </div>
            </div>

          </template>

          <div v-else class="guide-detail-empty">
            Select a guide from the list or click Add to create one.
          </div>
        </AntCol>
      </AntRow>
    </AntCol>
  </AntRow>
</template>

<script>
import {
  ref,
  reactive,
  inject,
  watch,
  computed,
  nextTick,
} from 'vue'

import moment from 'moment'
import ShortUniqueId from 'short-unique-id'

import {
  Row as AntRow,
  Col as AntCol,
  Input as AntInput,
  Button as AntButton,
  Popconfirm as AntPopconfirm,
  message as antMessage,
} from 'ant-design-vue'

import AddIcon from '@ant-design/icons-vue/lib/icons/PlusOutlined'
import EditIcon from '@ant-design/icons-vue/lib/icons/EditOutlined'
import DeleteIcon from '@ant-design/icons-vue/lib/icons/DeleteOutlined'
import SaveIcon from '@ant-design/icons-vue/lib/icons/CheckOutlined'
import CancelIcon from '@ant-design/icons-vue/lib/icons/CloseOutlined'
import SearchIcon from '@ant-design/icons-vue/lib/icons/SearchOutlined'

import SectionEditor from '@/components/ApiDoc/editor/SectionEditor.vue'
import DevDebugDataBlock from '@/components/ApiDoc/shared/DevDebugDataBlock.vue'

import {
  createManagementItem,
  updateManagementItem,
  deleteManagementItem,
} from '@/api/apiDocV3'

import { useViewerEditor, contentToHtml } from '../shared/viewerEditor'

const ENTITY_TYPE = 'guide'

const escapeRegExp = str => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const splitHighlightFragments = (text, keyword) => {
  if (!keyword) return [{ text: text ?? '', highlight: false }]
  return String(text ?? '')
    .split(new RegExp(`(${escapeRegExp(keyword)})`, 'gi'))
    .filter(part => part !== '')
    .map(part => ({
      text: part,
      highlight: part.toLowerCase() === keyword.toLowerCase(),
    }))
}

const highlightHtmlContent = (html, keyword) => {
  if (!keyword?.trim() || !html) return html || ''
  const container = document.createElement('div')
  container.innerHTML = html
  const textNodes = []
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    textNodes.push(node)
    node = walker.nextNode()
  }
  textNodes.forEach((textNode) => {
    const parts = textNode.textContent.split(new RegExp(`(${escapeRegExp(keyword)})`, 'gi'))
    if (parts.length <= 1) return
    const fragment = document.createDocumentFragment()
    parts.forEach((part) => {
      if (!part) return
      if (part.toLowerCase() === keyword.toLowerCase()) {
        const mark = document.createElement('mark')
        mark.className = 'search-highlight'
        mark.textContent = part
        fragment.appendChild(mark)
      } else {
        fragment.appendChild(document.createTextNode(part))
      }
    })
    if (textNode.parentNode) {
      textNode.parentNode.replaceChild(fragment, textNode)
    }
  })
  return container.innerHTML
}

export default {
  name: 'UserGuide',
  components: {
    AntRow,
    AntCol,
    AntInput,
    AntButton,
    AntPopconfirm,
    SectionEditor,
    AddIcon,
    EditIcon,
    DeleteIcon,
    SaveIcon,
    CancelIcon,
    SearchIcon,
    DevDebugDataBlock,
  },
  props: {
    readOnly: Boolean,
    guidesData: {
      type: Array,
      default: () => [],
    },
    imageManagementData: {
      type: Array,
      default: () => [],
    },
    sectionsDataByDocId: {
      type: Object,
      default: () => ({}),
    },
    onChangeManagementData: Function,
  },
  setup(props) {
    const isDev = inject('isDev')
    useViewerEditor()

    const searchText = ref('')
    const appliedSearchText = ref('')
    const viewContentHtml = ref('')

    const guidesData = reactive([...(props.guidesData || [])])
    const filteredGuides = ref([...guidesData])
    const displayedGuides = computed(() => filteredGuides.value
      .slice()
      .sort((a, b) => (a.guideName || '').localeCompare(b.guideName || '')))

    const selectedGuideId = ref(displayedGuides.value[0]?.id || '')

    const isEditing = ref(false)
    const editState = reactive({
      guideName: '',
      content: '',
    })

    const syncFromProps = () => {
      guidesData.splice(0, guidesData.length, ...(props.guidesData || []))
      filteredGuides.value = [...guidesData]
      if (!guidesData.find(g => g.id === selectedGuideId.value)) {
        selectedGuideId.value = guidesData[0]?.id || ''
      }
    }

    watch(() => props.guidesData, syncFromProps, { deep: true })

    const selectedGuide = computed(() => guidesData.find(g => g.id === selectedGuideId.value))

    const getHighlightFragments = text => splitHighlightFragments(text, appliedSearchText.value)

    const refreshViewContentHtml = async () => {
      if (isEditing.value || !selectedGuide.value) {
        viewContentHtml.value = ''
        return
      }
      let html = await contentToHtml(selectedGuide.value.content || '')
      if (appliedSearchText.value) {
        html = highlightHtmlContent(html, appliedSearchText.value)
      }
      viewContentHtml.value = html
    }

    watch(
      [selectedGuideId, isEditing, () => selectedGuide.value?.content, appliedSearchText],
      refreshViewContentHtml,
      { immediate: true },
    )

    const selectFirstDisplayedGuide = () => {
      if (isEditing.value) return
      const [first] = displayedGuides.value
      selectedGuideId.value = first?.id || ''
    }

    const onSearch = () => {
      const keyword = searchText.value.trim()
      appliedSearchText.value = keyword
      const keywordLower = keyword.toLowerCase()
      if (!keywordLower) {
        filteredGuides.value = [...guidesData]
        if (!guidesData.some(g => g.id === selectedGuideId.value)) {
          selectFirstDisplayedGuide()
        }
        return
      }
      filteredGuides.value = guidesData.filter((g) => {
        const name = (g.guideName || '').toLowerCase()
        const content = (g.content || '').toLowerCase()
        return name.includes(keywordLower) || content.includes(keywordLower)
      })
      selectFirstDisplayedGuide()
    }

    watch(searchText, (val, oldVal) => {
      if (!val?.trim() && oldVal?.trim()) {
        onSearch()
      }
    })

    const resetEditState = (guide) => {
      editState.guideName = guide?.guideName || ''
      editState.content = guide?.content || ''
    }

    const hasEditChanges = () => {
      const guide = selectedGuide.value
      if (!guide) return false

      const name = (editState.guideName || '').trim()
      const content = editState.content || ''

      if (guide.isNew) {
        return !!name || !!content
      }

      return name !== (guide.guideName || '').trim()
        || content !== (guide.content || '')
    }

    const discardEmptyNewGuideIfNeeded = () => {
      const guide = selectedGuide.value
      if (!guide?.isNew) return
      const idx = guidesData.findIndex(g => g.id === guide.id)
      if (idx > -1) guidesData.splice(idx, 1)
      filteredGuides.value = filteredGuides.value.filter(g => g.id !== guide.id)
    }

    const onSelectGuide = (id) => {
      if (id === selectedGuideId.value) return

      if (isEditing.value) {
        if (hasEditChanges()) {
          antMessage.warning('請先儲存或取消目前的編輯')
          return
        }
        discardEmptyNewGuideIfNeeded()
        isEditing.value = false
      }

      selectedGuideId.value = id
    }

    const onStartEdit = () => {
      if (!selectedGuide.value) return
      resetEditState(selectedGuide.value)
      isEditing.value = true
    }

    const onCancelEdit = () => {
      const guide = selectedGuide.value
      if (guide?.isNew) {
        const idx = guidesData.findIndex(g => g.id === guide.id)
        if (idx > -1) guidesData.splice(idx, 1)
        filteredGuides.value = filteredGuides.value.filter(g => g.id !== guide.id)
        selectedGuideId.value = guidesData[0]?.id || ''
      }
      isEditing.value = false
    }

    const onAdd = () => {
      const newGuide = {
        id: new ShortUniqueId({ length: 10 }).rnd(),
        entityType: ENTITY_TYPE,
        guideName: '',
        content: '',
        isNew: true,
      }
      guidesData.unshift(newGuide)
      filteredGuides.value = [...guidesData]
      selectedGuideId.value = newGuide.id
      resetEditState(newGuide)
      isEditing.value = true

      nextTick(() => {
        const input = document.querySelector('.guide-name-input input')
        if (input) input.focus()
      })
    }

    const onSave = async () => {
      const guide = selectedGuide.value
      if (!guide) return

      const guideName = editState.guideName?.trim()
      if (!guideName) {
        antMessage.warning('Please enter a title')
        return
      }

      const messageKey = `${Date.now()}`
      antMessage.loading('', { key: messageKey })

      const isCreate = !!guide.isNew
      const payload = {
        id: guide.id,
        entityType: ENTITY_TYPE,
        guideName,
        content: editState.content || '',
        ...(isCreate && { createdTime: moment().toISOString(true) }),
      }

      let data
      let message
      if (isCreate) {
        ({ data, message } = await createManagementItem(payload))
      } else {
        ({ data, message } = await updateManagementItem(payload))
      }

      if (data) {
        const idx = guidesData.findIndex(g => g.id === guide.id)
        if (idx > -1) {
          guidesData[idx] = { ...data }
          delete guidesData[idx].isNew
        }
        filteredGuides.value = [...guidesData]
        if (props.onChangeManagementData) {
          props.onChangeManagementData(ENTITY_TYPE, [...guidesData])
        }
        isEditing.value = false
        antMessage.success({
          key: messageKey,
          content: `${isCreate ? 'Create' : 'Update'} guide [${guideName}] successfully`,
        })
      } else {
        antMessage.error({
          key: messageKey,
          duration: 5,
          content: `Failed to ${isCreate ? 'create' : 'update'} guide: ${message}`,
        })
      }
    }

    const onDelete = async () => {
      const guide = selectedGuide.value
      if (!guide || guide.isNew) return

      const messageKey = `${Date.now()}`
      antMessage.loading('', { key: messageKey })

      const { data, message } = await deleteManagementItem({
        entityType: ENTITY_TYPE,
        id: guide.id,
      })

      if (data) {
        const idx = guidesData.findIndex(g => g.id === guide.id)
        if (idx > -1) guidesData.splice(idx, 1)
        filteredGuides.value = filteredGuides.value.filter(g => g.id !== guide.id)
        selectedGuideId.value = guidesData[0]?.id || ''
        isEditing.value = false
        if (props.onChangeManagementData) {
          props.onChangeManagementData(ENTITY_TYPE, [...guidesData])
        }
        antMessage.success({
          key: messageKey,
          content: `Delete guide [${guide.guideName}] successfully`,
        })
      } else {
        antMessage.error({
          key: messageKey,
          duration: 5,
          content: `Failed to delete guide [${guide.guideName}]: ${message}`,
        })
      }
    }

    return {
      isDev,
      searchText,
      getHighlightFragments,
      onSearch,
      onAdd,
      displayedGuides,
      selectedGuideId,
      selectedGuide,
      viewContentHtml,
      isEditing,
      editState,
      onSelectGuide,
      onStartEdit,
      onCancelEdit,
      onSave,
      onDelete,
    }
  },
}
</script>

<style scoped>
.search-row {
  margin-bottom: 24px;
  flex-shrink: 0;
}

.user-guide {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.user-guide > :deep(.ant-col) {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.guide-body {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  border: 1px solid #d9d9d9;
  align-items: stretch;
  overflow: hidden;
  flex-wrap: nowrap;
}

.guide-body > :deep(.ant-col) {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.guide-col {
  padding: 16px 12px;
  border-right: 1px solid #d9d9d9;
  min-height: 0;
  overflow: hidden;
}

.guide-col:last-child {
  border-right: none;
}

.guide-detail-col {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  height: 100%;
  overflow: hidden;
}

.guide-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.guide-list-item {
  padding: 4px 12px;
  cursor: pointer;
  border-radius: 4px;
  word-break: break-word;
}

.guide-list-item:hover {
  background: #f5f5f5;
}

.guide-list-item--selected {
  background: #e6f7ff;
  color: #1890ff;
}

.guide-list-empty,
.guide-detail-empty {
  padding: 24px;
  color: rgba(0, 0, 0, 0.45);
}

.guide-detail-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  flex-shrink: 0;
}

.guide-detail-header--bordered {
  border-bottom: 1px solid #d9d9d9;
  padding-bottom: 12px;
}

.guide-detail-title {
  margin: 0;
  flex: 1;
  font-size: 18px;
}

.guide-name-input {
  flex: 1;
}

.guide-detail-actions .anticon {
  cursor: pointer;
  margin-left: 12px;
  font-size: 16px;
}

.guide-detail-actions .anticon:hover {
  color: #1890ff;
}

.guide-editor-wrap {
  flex: 1 1 0;
  min-height: 0;
  height: 0;
  overflow: hidden;
}

.guide-editor-scroll {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

.search-highlight {
  background-color: #ffe58f;
  padding: 0;
}

.guide-editor-scroll :deep(.search-highlight) {
  background-color: #ffe58f;
  padding: 0;
}
</style>
